import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const Navigation = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div 
          className="md:hidden"
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: isDarkMode ? '#000000' : '#ffffff',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}
        >
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: '2rem',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                style={{
                  fontSize: '1.875rem',
                  fontWeight: 300,
                  color: isDarkMode 
                    ? (isActive(link.path) ? '#ffffff' : '#9ca3af')
                    : (isActive(link.path) ? '#000000' : '#6b7280'),
                  textDecoration: 'none'
                }}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}

      <nav style={{ position: 'fixed', top: '2rem', left: 0, right: 0, padding: '0 2rem', zIndex: isOpen ? 10000 : 50 }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center group" onClick={() => setIsOpen(false)}>
              <img 
                src="/logo.png" 
                alt="onlzCode Logo" 
                className={`h-20 md:h-28 lg:h-36 w-auto transition-all duration-300 group-hover:scale-105 ${!isDarkMode ? 'invert' : ''}`}
              />
            </Link>

            <div className="hidden md:flex items-center space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 text-sm transition-colors duration-300 relative group ${
                    isDarkMode
                      ? (isActive(link.path) ? 'text-white' : 'text-gray-400 hover:text-white')
                      : (isActive(link.path) ? 'text-gray-900' : 'text-gray-500 hover:text-gray-900')
                  }`}
                >
                  <span className="relative z-10">{link.label}</span>
                  {isActive(link.path) && (
                    <span className={`absolute inset-0 ${isDarkMode ? 'bg-white/5' : 'bg-black/5'} rounded-md`} />
                  )}
                  <span className={`absolute bottom-0 left-4 right-4 h-px ${isDarkMode ? 'bg-white' : 'bg-gray-900'} scale-x-0 transition-transform duration-300 group-hover:scale-x-100`} />
                </Link>
              ))}

              {/* Theme Toggle Button */}
              <button
                onClick={toggleTheme}
                className={`ml-4 p-2 rounded-full transition-all duration-300 ${
                  isDarkMode 
                    ? 'text-gray-400 hover:text-white hover:bg-white/10' 
                    : 'text-gray-500 hover:text-gray-900 hover:bg-black/10'
                }`}
                aria-label="Toggle theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
            </div>

            <div className="md:hidden flex items-center space-x-2">
              {/* Mobile Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 transition-colors duration-300 ${
                  isDarkMode 
                    ? 'text-white hover:text-gray-300' 
                    : 'text-gray-900 hover:text-gray-600'
                }`}
                aria-label="Toggle theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>

              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`p-2 transition-colors duration-300 ${
                  isDarkMode 
                    ? 'text-white hover:text-gray-300' 
                    : 'text-gray-900 hover:text-gray-600'
                }`}
                aria-label="Toggle menu"
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
